import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import {
	AvailableFilters,
	CampaignPayload,
	Campaign,
	CasesWithCampaign,
	ClientTargetingResponse,
	ClientRetargetingResponse,
	HttpResponse,
	CampaignTableData,
	CampaignSchedule,
	PendingSchedule,
	AIPMarketingPayload,
	AIPMarketingData,
	AIPMarketingSinglePayload,
	ChannelsAdsData,
	MarketingImageData,
	MarketingImagePayload,
	CampaignTableRequest,
	CasesCampaigns,
	PendingPaidSchedule,
	SaveSchedulePayload,
	MarketingMetricData,
	ChannelsData,
	SendMarketingEmailPaylod,
	CampaignChannels,
	ClientRetargetingParams,
	EmailCampaignsPayload,
	ClientCalculation,
	RetargetingListCases,
	EmailCampaigns,
} from '@/interfaces';
import {
	AIPromptData,
	AIPromptPayload,
	ClientTargetingParams,
} from '@/interfaces';

class MarketingService {
	constructor(private client: AxiosInstance) {}
	async postAIPrompt(
		data: AIPromptPayload
	): Promise<HttpResponse<AIPromptData>> {
		return this.client.post(`/chat-bot/free-marketing`, data);
	}

	async getClientTargeting({
		page,
		limit,
		categoryId,
		brandId,
		sortClientsDesc,
		dateFrom,
		dateTo,
		search,
	}: ClientTargetingParams): Promise<HttpResponse<ClientTargetingResponse>> {
		const params = {
			page,
			limit,
			...(dateFrom && { startDate: dateFrom }),
			...(dateTo && { endDate: dateTo }),
			...(search && { brandName: search }),
			...(categoryId && { categoryId }),
			...(brandId && { brandId }),
			...(sortClientsDesc && { sortClientsDesc }),
		};
		return this.client(`/marketing/client-targeting`, { params });
	}

	async getClientRetargeting({
		page,
		limit,
		search,
	}: ClientRetargetingParams): Promise<
		HttpResponse<ClientRetargetingResponse>
	> {
		const params = {
			page,
			limit,
			...(search && { search }),
		};
		return this.client(`/marketing/client-retargeting`, { params });
	}

	async getEmailCampaigns(id: string): Promise<HttpResponse<EmailCampaigns>> {
		return this.client(`/marketing/email_campaigns/${id}`);
	}

	// --------------- Campaign -------------------//
	async getAllCampaigns({
		page,
		limit,
		status,
		search,
		caseId,
		channel,
		end_date,
		ad_spend,
		start_date,
		audience_reach,
	}: CampaignTableRequest): Promise<HttpResponse<CampaignTableData>> {
		const params = {
			page,
			limit,
			...(start_date && { start_date }),
			...(end_date && { end_date }),
			...(caseId && { case_id: caseId }),
			...(status && { status: status.toUpperCase() }),
			...(search && { search: search.toLowerCase() }),
			...(channel && { channel: channel.toUpperCase() }),
			...(ad_spend && { ad_spend: ad_spend.toUpperCase() }),
			...(audience_reach && { audience_reach: audience_reach.toUpperCase() }),
		};

		return this.client.get(`/marketing/campaigns`, { params });
	}
	async getCampaignById(id: string): Promise<HttpResponse<Campaign>> {
		return this.client.get(`/marketing/campaigns/${id}`);
	}
	async getChannelsType(): Promise<HttpResponse<ChannelsData>> {
		return this.client.get(`/marketing/type-channel/media`);
	}
	async getSearchImages(
		term: string,
		limit: number,
		page: number,
		filterLandspace?: boolean
	): Promise<HttpResponse<MarketingImageData>> {
		const params = [];
		params.push(`limit=${limit}`);
		params.push(`page=${page}`);
		params.push('order=relevance');
		if (filterLandspace) {
			params.push(`filters%255Borientation%255D%255Blandscape%255D=1`);
			params.push(`filters%255Borientation%255D%255Bpanoramic%255D=1`);
		}

		// params.push(`filters%255Bcontent_type%255D%255Bphoto%255D=1`);
		// params.push(`filters%255Bcontent_type%255D%255Bpsd%255D=1`);

		// params.push(`filters%255Bcontent_type%255D%255Bvector%255D=1`);
		// params.push('filters%255Blicense%255D%255Bpremium%255D=1');
		const paramString = params.join('&');
		return this.client.get(`/marketing/search?term=${term}&${paramString}`);
	}
	async postCreateCampaign(
		data: CampaignPayload
	): Promise<HttpResponse<Campaign>> {
		return this.client.post('/marketing/campaigns', data);
	}
	async patchCreateCampaign(
		id: string,
		data: CampaignPayload
	): Promise<HttpResponse<Campaign>> {
		return this.client.patch(`/marketing/campaigns/${id}`, data);
	}
	async deleteCampaign(id: string): Promise<HttpResponse<Campaign>> {
		return this.client.delete(`/marketing/campaigns/${id}`);
	}

	// ---------------- Content Generation ----------------//

	async postMarkentingMultipleChannels(
		data: AIPMarketingPayload
	): Promise<HttpResponse<AIPMarketingData[]>> {
		return this.client.post(`/chat-bot/various-marketing`, data);
	}

	async postMarkentingSingleChannel(
		data: AIPMarketingSinglePayload
	): Promise<HttpResponse<AIPMarketingData>> {
		return this.client.post(`/chat-bot/marketing`, data);
	}

	async patchUploadCopies(
		id: string,
		data: ChannelsAdsData
	): Promise<HttpResponse<AIPMarketingData>> {
		return this.client.patch(`/marketing/campaigns/${id}/ads`, data);
	}

	async patchUploadImagesToCampaign(
		id: string,
		data: MarketingImagePayload
	): Promise<HttpResponse<AIPMarketingData>> {
		return this.client.patch(`/marketing/campaigns/${id}/images`, data);
	}

	async deleteAds(id: string): Promise<HttpResponse<Campaign>> {
		return this.client.delete(`/marketing/deleteChannels/${id}`);
	}

	async deleteMarketingImage(
		id: string,
		channel?: CampaignChannels
	): Promise<HttpResponse<Campaign>> {
		return this.client.delete(`/marketing/campaigns/${id}/images`, {
			data: { channel },
		});
	}

	// ----------------- ---------------//
	async getCasesWithCampaign(
		caseId?: string
	): Promise<HttpResponse<CasesWithCampaign[]>> {
		return this.client.get(`/marketing/cases-with-campaigns?caseId=${caseId}`);
	}

	async getCasesWithCampaignEmail(
		caseId?: string
	): Promise<HttpResponse<CasesWithCampaign[]>> {
		const auxParams = [];
		if (caseId) auxParams.push(`caseId=${caseId}`);
		const params = auxParams.length ? `?${auxParams.join('&')}` : '';
		return this.client.get(`/marketing/cases-with-campaigns-email${params}`);
	}

	async getAvailableFilters(): Promise<HttpResponse<AvailableFilters>> {
		return this.client.get(`/marketing/available-filters`);
	}
	async sendMarketingEmail(
		payload: SendMarketingEmailPaylod
	): Promise<HttpResponse<any>> {
		return this.client.post(`/marketing/send-marketing-email`, payload);
	}
	async approveCampaign(id: string): Promise<HttpResponse<string>> {
		return this.client.patch(`/marketing/campaigns/ads/${id}/approve`);
	}

	async closeCampaign(id: string): Promise<HttpResponse<string>> {
		return this.client.patch(`/marketing/campaigns/${id}/close`);
	}

	async getSchedulePost(
		campaignId: string
	): Promise<HttpResponse<CampaignSchedule[]>> {
		return this.client.get(`/marketing/scheduled-posts/${campaignId}`);
	}
	async getPendingSchedulePost(
		campaignId: string
	): Promise<HttpResponse<PendingSchedule[]>> {
		return this.client.get(`/marketing/scheduled-pending-posts/${campaignId}`);
	}
	async getPendingPaidSchedulePost(
		campaignId: string
	): Promise<HttpResponse<PendingPaidSchedule[]>> {
		return this.client.get(
			`/marketing/scheduled-paid-pending-posts/${campaignId}`
		);
	}
	async deletePendingSchedulePost(
		adId: string
	): Promise<HttpResponse<PendingSchedule[]>> {
		return this.client.delete(`/marketing/schedule-posts/${adId}`);
	}
	async postSchedule(
		scheduledData: SaveSchedulePayload
	): Promise<HttpResponse<any>> {
		return this.client.post('marketing/schedule-posts', scheduledData);
	}
	async postPaidSchedule(
		scheduledData: SaveSchedulePayload
	): Promise<HttpResponse<any>> {
		return this.client.post('marketing/schedule-paid-posts', scheduledData);
	}
	async savePaidSchedule(
		scheduledData: SaveSchedulePayload
	): Promise<HttpResponse<any>> {
		return this.client.patch(
			'marketing/schedule-paid-posts/save',
			scheduledData
		);
	}
	async savePostsSchedule(
		scheduledData: SaveSchedulePayload
	): Promise<HttpResponse<any>> {
		return this.client.patch('marketing/schedule-posts/save', scheduledData);
	}
	async getCasesCampaign(): Promise<HttpResponse<CasesCampaigns[]>> {
		return this.client.get(`/marketing/campaigns/case-filter`);
	}
	async getMarketingMetrics(
		caseID: string
	): Promise<HttpResponse<MarketingMetricData>> {
		return this.client.get(`/marketing-metrics?caseID=${caseID}`);
	}

	// ---------------- Retargeting ----------------//

	async createEmailCampaigns(
		data: EmailCampaignsPayload
	): Promise<HttpResponse<any>> {
		return this.client.post(`/marketing/email_campaigns`, data);
	}

	async updateEmailCampaign(
		id: string,
		data: EmailCampaignsPayload
	): Promise<HttpResponse<any>> {
		return this.client.patch(`/marketing/email_campaigns/${id}`, data);
	}

	async getClientCalculation(
		data: ClientCalculation
	): Promise<HttpResponse<any>> {
		const parsedData = {
			...(data.case_status && { case_status: data.case_status.toUpperCase() }),
			...(data.state_id && { state_id: data.state_id }),
			...(data.category_id && { category_id: data.category_id }),
			...(data.recency && { recency: data.recency }),
		};
		return this.client.post(`/marketing/email_campaigns/client_calculation`, {
			...parsedData,
		});
	}

	async getRetargetingCases(): Promise<HttpResponse<RetargetingListCases[]>> {
		return this.client.get(`/marketing/cases`);
	}
}

export const marketingService = new MarketingService(axiosClient);
